import { createFileRoute, Link } from "@tanstack/react-router";

const SITE_URL = "https://letslistbusiness.com";

export const Route = createFileRoute("/login/")({
  head: () => ({
    meta: [
      { title: "Log in | LetsListBusiness" },
      { name: "description", content: "Log in to your LetsListBusiness account to write reviews, save businesses and manage your listings." },
      { name: "robots", content: "noindex, follow" },
    ],
    links: [{ rel: "canonical", href: SITE_URL + "/login/" }],
  }),
  component: LoginPage,
});

function LoginPage() {
  return (
    <section className="container mx-auto grid gap-0 px-4 py-10 lg:grid-cols-2 lg:gap-10">
      {/* Left — image panel */}
      <aside className="relative hidden overflow-hidden rounded-3xl lg:block">
        <img
          src="https://images.unsplash.com/photo-1521017432531-fbd92d768814?w=1200&q=80&auto=format&fit=crop"
          alt=""
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-hero opacity-80 mix-blend-multiply" />
        <div className="relative flex h-full min-h-[520px] flex-col justify-between p-10 text-white">
          <div className="text-xs font-semibold uppercase tracking-[0.3em] text-white/80">LetsListBusiness</div>
          <div>
            <h2 className="font-display text-4xl font-bold leading-tight">
              Welcome back to your <em className="not-italic text-accent">neighborhood.</em>
            </h2>
            <p className="mt-4 max-w-sm text-white/85">
              Pick up where you left off — your saved spots, reviews and lists are right here.
            </p>
          </div>
        </div>
      </aside>

      {/* Right — form */}
      <div className="flex items-center">
        <div className="mx-auto w-full max-w-md">
          <h1 className="font-display text-4xl font-bold">Log in</h1>
          <p className="mt-2 text-sm text-muted-foreground">Welcome back. Let's get you in.</p>

          <form className="mt-8 space-y-4">
            <Field label="Email"><input className="field" type="email" placeholder="you@example.com" /></Field>
            <Field label="Password"><input className="field" type="password" placeholder="••••••••" /></Field>
            <div className="flex items-center justify-between text-xs">
              <label className="flex items-center gap-2 text-muted-foreground">
                <input type="checkbox" className="rounded border-border" /> Remember me
              </label>
              <a className="text-primary hover:underline">Forgot password?</a>
            </div>
            <button type="button" className="w-full rounded-full bg-primary py-3 text-sm font-semibold text-primary-foreground">Log in</button>
          </form>

          <div className="mt-6 flex items-center gap-3">
            <div className="h-px flex-1 bg-border" />
            <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">or</span>
            <div className="h-px flex-1 bg-border" />
          </div>
          <button className="mt-6 w-full rounded-full border border-border bg-card py-3 text-sm font-semibold hover:bg-muted">
            Continue with Google
          </button>

          <p className="mt-8 text-center text-sm text-muted-foreground">
            New here? <Link to="/signup" className="font-semibold text-primary underline">Create an account</Link>
          </p>
        </div>
      </div>
      <style>{`.field{width:100%;border-radius:.625rem;border:1px solid var(--border);background:var(--background);padding:.75rem .9rem;font-size:.875rem;outline:none}.field:focus{border-color:var(--ring);box-shadow:0 0 0 3px color-mix(in oklab, var(--ring) 25%, transparent)}`}</style>
    </section>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-[10px] font-semibold uppercase tracking-[0.15em] text-muted-foreground">{label}</span>
      {children}
    </label>
  );
}
